import React, { useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export const AuthCallback: React.FC = () => {
  const { isAuthenticated, authStatus } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('Completing sign in...');
  const authChannelRef = useRef<BroadcastChannel | null>(null);
  const responseReceivedRef = useRef(false);
  const [showContinue, setShowContinue] = useState(false);

  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    // Set up broadcast channel to communicate with other tabs
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel('auth');
      authChannelRef.current = channel;

      // Listen for auth success from other tabs
      channel.onmessage = (event) => {
        if (event.data.type === 'auth_success') {
          responseReceivedRef.current = true;
          console.log('[AuthCallback] Auth success detected from another tab');
          setMessage('Sign in successful! You can close this tab.');

          // Try to close the tab after a short delay
          setTimeout(() => {
            window.close();
            // If window.close() doesn't work (common in modern browsers), show instruction
            setMessage(
              'Sign in successful! You can now close this tab and return to the application.'
            );
          }, 1500);
        }
      };

      timeout = setTimeout(() => {
        if (!responseReceivedRef.current) {
          setShowContinue(true);
        }
      }, 4000);
    } else {
      console.warn('[AuthCallback] BroadcastChannel not available; auth coordination disabled');
      setShowContinue(true);
    }

    const handleCallback = async () => {
      console.log('[AuthCallback] Processing auth callback...');

      try {
        // Support both hash and search params for mobile email clients
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);

        const getParam = (name: string) => searchParams.get(name) || hashParams.get(name);

        const accessToken = getParam('access_token');
        const refreshToken = getParam('refresh_token');
        const errorParam = getParam('error');

        console.log('[AuthCallback] Tokens found:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          hasError: !!errorParam,
        });

        if (errorParam) {
          console.error('[AuthCallback] Error returned in callback:', errorParam);
          setError(errorParam);
          setIsProcessing(false);
        } else if (accessToken && refreshToken) {
          // Manually set the session
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('[AuthCallback] Error setting session:', error);
            setError('Failed to complete sign in. Please try again.');
            setIsProcessing(false);
          } else {
            console.log('[AuthCallback] Session set successfully:', data);
            // Broadcast success to other tabs
            channel?.postMessage({ type: 'auth_success' });
            // Clear any auth params from the URL and redirect to home
            window.history.replaceState(null, '', '/');
            // Let Supabase auth state change handle the rest
            // Don't set isProcessing to false here, let auth completion handle it
          }
        } else {
          console.log('[AuthCallback] No tokens in URL, may already be authenticated');
          setMessage('Checking authentication status...');
          // Give auth context a moment to process
          setTimeout(() => {
            setIsProcessing(false);
          }, 1000);
        }
      } catch (err) {
        console.error('[AuthCallback] Unexpected error:', err);
        setError('An unexpected error occurred. Please try again.');
        setIsProcessing(false);
      }
    };

    handleCallback();

    return () => {
      channel?.close();
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  // Check authentication status more thoroughly
  useEffect(() => {
    // If authenticated and we're still on callback page, redirect
    if (isAuthenticated && window.location.pathname === '/auth/callback') {
      console.log('[AuthCallback] User authenticated, redirecting to home');
      window.location.href = '/';
    }
  }, [isAuthenticated]);

  // If authenticated, show a brief message before redirect
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#050614] to-[#0f1a2f]">
        <div className="text-center">
          <div className="text-green-400 text-xl mb-2">✓</div>
          <p className="text-gray-400">Sign in successful, redirecting...</p>
        </div>
      </div>
    );
  }

  // Show loading state or waiting for auth
  if (isProcessing || authStatus === 'authenticating') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#050614] to-[#0f1a2f]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4f46e5] mx-auto"></div>
          <p className="mt-4 text-gray-400">{message}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#050614] to-[#0f1a2f]">
        <div className="bg-[#0c1222] border border-[#1a2d4d] rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
          <button
            onClick={() => (window.location.href = '/')}
            className="px-4 py-2 bg-[#4f46e5] text-white rounded-lg hover:bg-[#4338ca] transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Fallback - if we're on the callback page but not processing and not authenticated
  // This means auth has likely already been processed by Supabase
  if (window.location.pathname === '/auth/callback') {
    console.log('[AuthCallback] On callback page, checking auth status:', {
      authStatus,
      isAuthenticated,
    });

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#050614] to-[#0f1a2f]">
        <div className="bg-[#0c1222] border border-[#1a2d4d] rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">✨</div>
            <h1 className="text-2xl font-bold text-white mb-2">Authentication Complete</h1>
            <p className="text-gray-400 mb-6">
              Sign in was successful! You can close this tab and return to the original tab where
              you started the login process.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                window.close();
                // If close doesn't work, try to navigate to the app
                setTimeout(() => {
                  window.location.href = '/';
                }, 100);
              }}
              className="w-full px-4 py-3 bg-[#4f46e5] text-white rounded-lg hover:bg-[#4338ca] transition-colors font-medium"
            >
              Close This Tab
            </button>

            {showContinue && (
              <button
                onClick={async () => {
                  await supabase.auth.getSession();
                  window.location.href = '/';
                }}
                className="w-full px-4 py-3 bg-[#1a2d4d] text-gray-300 rounded-lg hover:bg-[#223451] transition-colors"
              >
                Continue in This Tab
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-6">
            {`If this tab doesn't close automatically, you can safely close it manually.`}
          </p>
        </div>
      </div>
    );
  }

  return null;
};
